// 管理画面の受講履歴（resources/views/admin/learning_history/index.blade.php）の日ごとの詳細パネルの開閉
// - .js-menu（日ごとのカード）をクリック／Enter・Space で、直後の .js-menu-panel を高さのアニメーションで開閉する
// - 開閉アイコン（.icon-plus）は開いているあいだ 45度回転させて × にする
// - 受講生側の受講履歴は独自のスクリプトで開閉するため、直後に .js-menu-panel があるカードだけを対象にする
// - 動きを減らす設定のときはアニメーションしない

/** 開閉の時間の最小・最大（ミリ秒）。中身が高いほど長くして、速さが一定に見えるようにする */
const MIN_DURATION_MS = 350
const MAX_DURATION_MS = 700
/** 開くときは最後にゆっくり止まる、閉じるときはなめらかに加速して止まる */
const EASING_OPEN = 'cubic-bezier(0.22, 1, 0.36, 1)'
const EASING_CLOSE = 'cubic-bezier(0.4, 0, 0.2, 1)'

/**
 * 高さに応じた開閉の時間
 *
 * @param {number} height
 * @returns {number}
 */
const durationFor = (height) => Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, 250 + height * 0.25))

/**
 * 動きを減らす設定かどうか
 *
 * @returns {boolean}
 */
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * パネルを開く（高さ 0 → 中身の高さ → auto）
 *
 * @param {HTMLElement} panel
 * @returns {void}
 */
const open = (panel) => {
    panel.classList.remove('hidden')
    if (prefersReducedMotion()) {
        return
    }
    animateHeight(panel, 0, panel.scrollHeight, EASING_OPEN, () => {
        panel.style.height = ''
    })
}

/**
 * パネルを閉じる（今の高さ → 0 → hidden）
 *
 * @param {HTMLElement} panel
 * @returns {void}
 */
const close = (panel) => {
    if (prefersReducedMotion()) {
        panel.classList.add('hidden')
        return
    }
    animateHeight(panel, panel.scrollHeight, 0, EASING_CLOSE, () => {
        panel.classList.add('hidden')
        panel.style.height = ''
    })
}

/**
 * 高さを from → to へアニメーションさせ（中身は同時にフェード）、終わったら done を呼ぶ
 * （フェードは .js-menu-panel-content にだけかける。パネル全体を透かすと、閉じる途中で背後の点線が見えてしまうため）
 * （requestAnimationFrame は裏のタブで止まるため使わず、強制再計算で開始位置を確定させる。
 *   transitionend が来ない場合に備えて、時間経過でも終了処理を行う）
 *
 * @param {HTMLElement} panel
 * @param {number} from
 * @param {number} to
 * @param {string} easing
 * @param {Function} done
 * @returns {void}
 */
const animateHeight = (panel, from, to, easing, done) => {
    const duration = durationFor(Math.max(from, to))
    const content = panel.querySelector('.js-menu-panel-content') || panel
    // 連続クリックで前回のアニメーションの終了処理が残っていれば打ち切る
    if (panel._heightTimer) {
        clearTimeout(panel._heightTimer)
    }
    panel.style.transition = 'none'
    content.style.transition = 'none'
    panel.style.height = `${from}px`
    content.style.opacity = from === 0 ? '0' : '1'
    // 開始位置を確定させる（強制再計算）
    void panel.offsetHeight
    // 中身のフェードは高さより少し短くする
    panel.style.transition = `height ${duration}ms ${easing}`
    content.style.transition = `opacity ${Math.round(duration * 0.7)}ms ${easing}`
    panel.style.height = `${to}px`
    content.style.opacity = to === 0 ? '0' : '1'

    let finished = false
    const finish = () => {
        if (finished) {
            return
        }
        finished = true
        clearTimeout(panel._heightTimer)
        panel._heightTimer = null
        panel.style.transition = ''
        content.style.transition = ''
        content.style.opacity = ''
        done()
    }
    // 高さの transition の終わりで終了する（opacity の transitionend は無視する）
    const onEnd = (event) => {
        if (event.target === panel && event.propertyName === 'height') {
            panel.removeEventListener('transitionend', onEnd)
            finish()
        }
    }
    panel.addEventListener('transitionend', onEnd)
    panel._heightTimer = setTimeout(() => {
        panel.removeEventListener('transitionend', onEnd)
        finish()
    }, duration + 50)
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.js-menu').forEach((menu) => {
        const panel = menu.nextElementSibling
        if (!panel || !panel.classList.contains('js-menu-panel')) {
            return
        }
        const icon = menu.querySelector('.icon-plus')

        const toggle = () => {
            const toOpen = panel.classList.contains('hidden')
            if (toOpen) {
                open(panel)
            } else {
                close(panel)
            }
            menu.setAttribute('aria-expanded', toOpen ? 'true' : 'false')
            if (icon) {
                // Tailwind のクラスは JS から増やせない（resources/js は読み取り対象外）ため、回転は style で指定する
                icon.style.transform = toOpen ? 'translateY(-50%) rotate(45deg)' : ''
            }
        }

        menu.addEventListener('click', toggle)
        menu.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                toggle()
            }
        })
    })
})
